import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import * as fns from 'date-fns'
import {useTranslation} from 'react-i18next'
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  makeStyles,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
} from '@material-ui/core'
import {TYPE_WHITE_LIST} from 'constants/CommonConstant'
import {getFeatures} from 'api/storageApi'
import {uploadInvoiceFileToS3} from 'api/productApi'
import {navigate} from 'AppHistory'
import RouteConstant from 'constants/RouteConstant'
import {
  edfImportAnalysisRequestApi,
  getListAssignedDeviceApi,
  uploadMeasureFileApi,
} from 'api/orgApi'
import {ZScore} from 'api/analysisApi'
import CardWithTitle from 'components/atoms/CardWithTitle'
import FileDropzone from 'pages/Analysis/EDFImport/FileDropzone/FileDropzone'
import CalenderDatePicker from 'components/Calender/CalenderPicker'
import useProductManagement from 'features/productManagement/useProductManagement'
import useFailureModal from 'features/modal/useFailureModal'
import {NORM_PACKAGE_ID} from 'pages/ProductOrderPages/constants'
import {
  EEG_BRAIN_CONNECTIVITY_ID,
  EEG_DEMENTIA_PREDICTION_5_ID,
  EEG_DEMENTIA_PREDICTION_8_ID,
  EEG_FIVE_INDICATOR_ID,
  EEG_INDICATOR_3D_ID,
  EEG_INDICATOR_ID,
  EEG_SUMMARY_ADVANCED_ID,
  EEG_SUMMARY_ID,
  TYPE_INDICATOR_PRODUCT,
} from 'pages/ProductsPages/constant'
import {PACKAGE_TYPE, TypePackageTypeValue} from 'constants/AnalysisConstant'
import type {ListPackageResponse} from 'api/productApi'
import {StyledFieldSet} from '../EEGIndividual/ReAnalysis/StyledFieldSet'
import PatientTable from './PatientTable'
import edfImportType from './style'
import ImportSuccessDialog from './ImportSuccessDialog'

const useStyles = makeStyles(edfImportType)

const Z_SCORE_OPTIONS: ZScore[] = [1.96, 2.58, 3]
const FileExtension = '.edf'

const EDFImport = () => {
  const [eyeStatus, setEyeStatus] = useState('EC')
  const [listCheckedPackage, setListCheckedPackage] = useState<string[]>([
    EEG_INDICATOR_ID,
  ])
  const [isWhiteList, setIsWhiteList] = useState<boolean>(false)
  const {t, i18n} = useTranslation()
  const classes = useStyles()
  const [reAnalysis, setReAnalysis] = useState<'auto' | 'manual'>('auto')
  const [zScore, setZScore] = useState('1.96')
  const [file, setFile] = useState<File | null>(null)
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [isShowSuccessDialog, setIsShowSuccessDialog] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [listManufacturer, setListManufacturer] = useState<string[]>([])
  const [successMessage, setSuccessMessage] = useState<{
    manufacturer: string
    deviceName: string
    fileName: string
  } | null>(null)
  const [listDevice, setListDevice] = useState<
    Record<
      string,
      {
        deviceId: number
        deviceName: string
      }[]
    >
  >({})
  const [deviceSelected, setDeviceSelected] = useState<{
    manufacturer: string
    deviceId: number
  }>({
    manufacturer: `- ${t('IManufacturingCompany')} -`,
    deviceId: -1,
  })
  const [measurementDate, setMeasurementDate] = useState<Date>(new Date())
  const [isEnableIndividual3d, setIsEnableIndividual3d] = useState(false)
  const [isEnableNorm3d, setIsEnableNorm3d] = useState(false)
  const [isSraYn, setIsSraYn] = useState(false)
  const [hasPatientError, setHasPatientError] = useState(false)
  const [hasFileError, setHasFileError] = useState({
    isError: false,
    message: '',
  })
  const [hasAnalysisTypeError, setHasAnalysisTypeError] = useState(false)
  const [hasDeviceError, setHasDeviceError] = useState(false)

  const {
    getAvailableLicenses,
    getPackageList,
    listAvailableLicenses,
    listPackage,
  } = useProductManagement()

  const availablePackageItems = useMemo(() => {
    const availablePackageIds = Array.from(
      new Set(listAvailableLicenses),
      (license) => license.packageItemId,
    )
    return (
      listPackage?.filter((item) =>
        availablePackageIds.includes(`${item.itemId}`),
      ) ?? []
    )
  }, [listAvailableLicenses, listPackage])

  const ReAnalysisRadioValue = [
    {label: 'IReAnalysis_auto', value: 'auto'},
    {label: 'IReAnalysis_manual', value: 'manual'},
  ]

  const {onOpen: onFailureModalOpen} = useFailureModal()

  const threeDRenderingRadioValue = [
    {
      label: 'on',
      value: 'on',
    },
    {
      label: 'off',
      value: 'off',
    },
  ]

  const listEyeStatus = [
    {value: 'EC', label: t('IEC')},
    {value: 'EO', label: t('IEO')},
  ]

  const handleReAnalysisTypeChanged = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.value === 'manual') {
      setIsEnableIndividual3d(false)
    }
    setReAnalysis(event.target.value as 'auto' | 'manual')
  }

  const addPreRequisites = (cls: string, types: TypePackageTypeValue[]) => {
    if (types.length === 0) return []

    const prerequisites: ListPackageResponse[] = []

    types.forEach((type) => {
      const add = availablePackageItems.filter(
        (item) => cls.includes(item.cls) && item.type === type,
      )
      if (add.length === 0) return
      prerequisites.push(...add)
      add.forEach((item) => {
        prerequisites.push(...addPreRequisites(item.cls, item.prerequisites))
      })
    })

    return prerequisites
  }

  const removePreRequisites = (cls: string, type?: TypePackageTypeValue) => {
    if (!type) return []

    const toRemove: ListPackageResponse[] = []
    const removal = availablePackageItems.filter(
      (item) =>
        item.cls === cls &&
        item.prerequisites.includes(type) &&
        item.target !== 'HealthCare',
    )
    toRemove.push(...removal)
    removal.forEach((item) => {
      toRemove.push(...removePreRequisites(item.cls, item.type))
    })

    return Array.from(new Set(toRemove)) // Remove duplicates
  }

  const onCheckPackage = ({
    target: {name: pkgId, checked},
  }: React.ChangeEvent<HTMLInputElement>) => {
    const selectedItem = availablePackageItems.find(
      (item) => `${item.itemId}` === pkgId,
    )
    if (!selectedItem) return

    setListCheckedPackage((prev) => {
      let newSelected = [...prev]
      if (checked) {
        // 택1 패키지 전처리
        if ([EEG_SUMMARY_ID, EEG_SUMMARY_ADVANCED_ID].includes(pkgId)) {
          newSelected = newSelected.filter(
            (id) => ![EEG_SUMMARY_ID, EEG_SUMMARY_ADVANCED_ID].includes(id),
          )
        } else if (
          [EEG_DEMENTIA_PREDICTION_5_ID, EEG_DEMENTIA_PREDICTION_8_ID].includes(
            pkgId,
          )
        ) {
          newSelected = newSelected.filter(
            (id) =>
              ![
                EEG_DEMENTIA_PREDICTION_5_ID,
                EEG_DEMENTIA_PREDICTION_8_ID,
              ].includes(id),
          )
        }

        newSelected.push(pkgId)
        newSelected.push(
          ...addPreRequisites(selectedItem.cls, [selectedItem.type]).map(
            (item) => `${item.itemId}`,
          ),
        )
      } else {
        newSelected = newSelected.filter((id) => id !== pkgId)
        const toRemove = removePreRequisites(
          selectedItem.cls,
          selectedItem.type,
        ).map((item) => `${item.itemId}`)
        newSelected = newSelected.filter((id) => !toRemove.includes(id))
      }
      return Array.from(new Set(newSelected))
    })
  }

  const listPackageItem = useMemo(() => {
    const uniquePackageItemIds = [
      ...new Set(listAvailableLicenses.map((l) => l.packageItemId)),
    ]
    return uniquePackageItemIds
      .map((packageItemId) => {
        const currentLicense = listPackage?.find(
          (item) => item.itemId === packageItemId,
        )
        if (!currentLicense) {
          return {
            packageItemId: String(packageItemId),
            cls: '',
            label: '',
            type: '',
            order: 0,
          }
        }
        const displayInfo = currentLicense.displayInfos?.find(
          (info) => info.langCode === (i18n.language === 'ko' ? 'ko' : 'en'),
        )
        return {
          packageItemId: String(currentLicense.itemId),
          cls: currentLicense.cls ?? '',
          label: displayInfo?.name ?? '',
          type: currentLicense.type,
          order: currentLicense.order,
        }
      })
      .filter((item) => item.cls.includes('EEG'))
      .sort((a, b) => a.order - b.order)
  }, [listAvailableLicenses, listPackage, i18n.language])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setZScore((event.target as HTMLInputElement).value)
  }

  const reportGenerationId = [
    EEG_SUMMARY_ID,
    EEG_SUMMARY_ADVANCED_ID,
    EEG_FIVE_INDICATOR_ID,
    EEG_BRAIN_CONNECTIVITY_ID,
  ]

  const aiDementiaPredictionId = [
    EEG_DEMENTIA_PREDICTION_5_ID,
    EEG_DEMENTIA_PREDICTION_8_ID,
  ]

  const calculatePackageAmount = useCallback(
    (packageId: string) => {
      const listLicensePackage = listAvailableLicenses.filter(
        (license) => String(license.packageItemId) === packageId,
      )
      const amount = listLicensePackage.reduce((acc, cur) => {
        return acc + (cur.offerQuantity + cur.priority - cur.usageQuantity)
      }, 0)
      return amount > 1000000 ? '' : `(${amount})`
    },
    [listAvailableLicenses],
  )

  const calculateAge = (toDate: Date, birth: string) => {
    if (!fns.isValid(toDate)) return NaN

    const birthDate = fns.parseISO(birth)
    if (!fns.isValid(birthDate)) return NaN

    const diffInDays = fns.differenceInDays(
      fns.startOfDay(toDate),
      fns.startOfDay(birthDate),
    )

    // 365.25로 나누고 소수점 2자리에서 반올림하여 나이를 계산
    const age = diffInDays / 365.25
    return Math.round(age * 100) / 100
  }

  const isAdult = useMemo(() => {
    if (!selectedPatient?.birth || !new Date(selectedPatient?.birth).getMonth) {
      return false
    }
    const Age = calculateAge(measurementDate, selectedPatient.birth)
    return Number.isNaN(Age) ? false : Age >= 20
  }, [measurementDate, selectedPatient?.birth])

  const renderIndividualSection = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                name={EEG_INDICATOR_ID}
                checked={listCheckedPackage.includes(EEG_INDICATOR_ID)}
                onChange={onCheckPackage}
                color='primary'
              />
            }
            label={
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: 'black',
                }}
              >
                Individual analysis {calculatePackageAmount(EEG_INDICATOR_ID)}
              </div>
            }
            style={{
              width: 'fit-content',
            }}
          />
        </div>
      </div>
      {listCheckedPackage.includes(EEG_INDICATOR_ID) && (
        <div className={classes.packageContainer}>
          <div
            style={{
              flex: 1,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              Denoising Method
              <RadioGroup
                defaultValue={reAnalysis}
                name='radio-buttons-group'
                style={{}}
                row
                onChange={handleReAnalysisTypeChanged}
              >
                {ReAnalysisRadioValue.map((item) => (
                  <FormControlLabel
                    key={item.value}
                    value={item.value}
                    control={<Radio color='primary' />}
                    label={t(item.label)}
                  />
                ))}
              </RadioGroup>
              <div className='input-area' style={{padding: '20px 10px'}}>
                {reAnalysis === 'manual' && (
                  <StyledFieldSet
                    direction='column'
                    style={{padding: '0 0 10px'}}
                  >
                    <label htmlFor='signalRestoration'>
                      <input
                        type='checkbox'
                        id='signalRestoration'
                        name='agree_auto_denoise'
                        checked={isSraYn}
                        onChange={() => setIsSraYn((b) => !b)}
                      />
                      <span className='label'>{t('ISignalRestoration')}</span>
                    </label>
                  </StyledFieldSet>
                )}
              </div>
            </div>
          </div>
          {listPackageItem.some(
            (item) => item.packageItemId === EEG_INDICATOR_3D_ID,
          ) && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
                flex: 1,
              }}
            >
              3D Rendering {calculatePackageAmount(EEG_INDICATOR_3D_ID)}
              <RadioGroup
                defaultValue='off'
                name='radio-buttons-group'
                row
                value={isEnableIndividual3d ? 'on' : 'off'}
                onChange={(e) => {
                  setIsEnableIndividual3d(e.target.value === 'on')
                }}
              >
                {threeDRenderingRadioValue.map((item) => (
                  <FormControlLabel
                    key={item.value}
                    value={item.value}
                    control={
                      <Radio
                        color='primary'
                        disabled={reAnalysis === 'manual'}
                      />
                    }
                    label={t(item.label)}
                  />
                ))}
              </RadioGroup>
            </div>
          )}
        </div>
      )}
    </div>
  )

  const renderNormSection = () => (
    <div className={classes.packageContainer}>
      <div
        style={{
          flex: 1,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                name={NORM_PACKAGE_ID[0]}
                checked={listCheckedPackage.includes(NORM_PACKAGE_ID[0])}
                onChange={onCheckPackage}
                disabled={reAnalysis === 'manual'}
                color='primary'
              />
            }
            label={
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: 'black',
                }}
              >
                Norm DB Comparison {calculatePackageAmount(NORM_PACKAGE_ID[0])}
              </div>
            }
            style={{
              width: 'fit-content',
            }}
          />
        </div>
        {listCheckedPackage.includes(NORM_PACKAGE_ID[0]) && (
          <div
            style={{
              display: 'flex',
              width: '100%',
            }}
          >
            <div
              style={{
                flex: 1,
              }}
            >
              <RadioGroup
                aria-label='gender'
                name='zScore'
                value={zScore}
                row
                onChange={handleChange}
              >
                {Z_SCORE_OPTIONS.map((value) => {
                  return (
                    <FormControlLabel
                      value={String(value)}
                      key={value}
                      control={<Radio color='primary' size='small' />}
                      label={String(value)}
                    />
                  )
                })}
              </RadioGroup>
            </div>
            {listPackageItem.some(
              (item) => item.packageItemId === NORM_PACKAGE_ID[1],
            ) && (
              <div
                style={{
                  flex: 1,
                }}
              >
                <div
                  style={{
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 5,
                      flex: 1,
                    }}
                  >
                    3D Rendering {calculatePackageAmount(NORM_PACKAGE_ID[1])}
                    <RadioGroup
                      defaultValue='off'
                      name='radio-buttons-group'
                      row
                      onChange={(e) => {
                        setIsEnableNorm3d(e.target.value === 'on')
                      }}
                    >
                      {threeDRenderingRadioValue.map((item) => (
                        <FormControlLabel
                          key={item.value}
                          value={item.value}
                          control={<Radio color='primary' />}
                          label={t(item.label)}
                        />
                      ))}
                    </RadioGroup>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )

  const renderSummarySection = () => (
    <div className={classes.packageContainer}>
      <div
        style={{
          width: '50%',
        }}
      >
        <FormControl component='fieldset'>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  name={EEG_SUMMARY_ID}
                  checked={listCheckedPackage.some((item) =>
                    reportGenerationId.includes(item),
                  )}
                  disabled={
                    reAnalysis === 'manual' || (isAdult && eyeStatus === 'EO')
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      onCheckPackage(e)
                    } else {
                      setListCheckedPackage((prev) =>
                        prev.filter(
                          (item) => !reportGenerationId.includes(item),
                        ),
                      )
                    }
                  }}
                  color='primary'
                />
              }
              label={
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: 'black',
                  }}
                >
                  Report generation
                </div>
              }
              style={{
                width: 'fit-content',
              }}
            />
          </div>
        </FormControl>
        {listCheckedPackage.some((item) =>
          reportGenerationId.includes(item),
        ) && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              marginLeft: 20,
            }}
          >
            {listPackageItem.some(
              (item) => item.packageItemId === EEG_SUMMARY_ID,
            ) && (
              <FormControlLabel
                control={
                  <Checkbox
                    name={EEG_SUMMARY_ID}
                    checked={listCheckedPackage.includes(EEG_SUMMARY_ID)}
                    onChange={onCheckPackage}
                    disabled={isAdult && eyeStatus === 'EO'}
                    color='primary'
                  />
                }
                label={
                  <div
                    style={{
                      fontSize: 16,
                    }}
                  >
                    Brain Function Mapping (Basic){' '}
                    {calculatePackageAmount(EEG_SUMMARY_ID)}
                  </div>
                }
                style={{
                  width: '46%',
                }}
              />
            )}

            {listPackageItem.some(
              (item) => item.packageItemId === EEG_SUMMARY_ADVANCED_ID,
            ) && (
              <FormControlLabel
                control={
                  <Checkbox
                    name={EEG_SUMMARY_ADVANCED_ID}
                    checked={listCheckedPackage.includes(
                      EEG_SUMMARY_ADVANCED_ID,
                    )}
                    disabled={isAdult && eyeStatus === 'EO'}
                    onChange={onCheckPackage}
                    color='primary'
                  />
                }
                label={
                  <div
                    style={{
                      fontSize: 16,
                    }}
                  >
                    Brain Function Mapping (Advanced){' '}
                    {calculatePackageAmount(EEG_SUMMARY_ADVANCED_ID)}
                  </div>
                }
                style={{
                  width: '46%',
                }}
              />
            )}
            {listPackageItem.some(
              (pkg) => pkg.packageItemId === EEG_FIVE_INDICATOR_ID,
            ) && (
              <FormControlLabel
                control={
                  <Checkbox
                    name={EEG_FIVE_INDICATOR_ID}
                    checked={listCheckedPackage.includes(EEG_FIVE_INDICATOR_ID)}
                    onChange={onCheckPackage}
                    disabled={eyeStatus === 'EO'}
                    color='primary'
                  />
                }
                label={
                  <div
                    style={{
                      fontSize: 16,
                    }}
                  >
                    5 Major Brain Function indices{' '}
                    {calculatePackageAmount(EEG_FIVE_INDICATOR_ID)}
                  </div>
                }
                style={{
                  width: '46%',
                }}
              />
            )}
            {listPackageItem.some(
              (item) => item.packageItemId === EEG_BRAIN_CONNECTIVITY_ID,
            ) && (
              <FormControlLabel
                control={
                  <Checkbox
                    name={EEG_BRAIN_CONNECTIVITY_ID}
                    checked={listCheckedPackage.includes(
                      EEG_BRAIN_CONNECTIVITY_ID,
                    )}
                    onChange={onCheckPackage}
                    disabled={eyeStatus === 'EO'}
                    color='primary'
                  />
                }
                label={
                  <div
                    style={{
                      fontSize: 16,
                    }}
                  >
                    Brain Function Connectivity{' '}
                    {calculatePackageAmount(EEG_BRAIN_CONNECTIVITY_ID)}
                  </div>
                }
                style={{
                  width: '46%',
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )

  const renderMciSection = () => (
    <div className={classes.packageContainer}>
      <div
        style={{
          width: '50%',
        }}
      >
        <FormControl component='fieldset'>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  name={EEG_DEMENTIA_PREDICTION_5_ID}
                  checked={listCheckedPackage.some((item) =>
                    aiDementiaPredictionId.includes(item),
                  )}
                  disabled={
                    !isAdult || eyeStatus === 'EO' || reAnalysis === 'manual'
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      onCheckPackage(e)
                    } else {
                      setListCheckedPackage((prev) =>
                        prev.filter(
                          (item) => !aiDementiaPredictionId.includes(item),
                        ),
                      )
                    }
                  }}
                  color='primary'
                />
              }
              label={
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: 'black',
                  }}
                >
                  AI Dementia Prediction
                </div>
              }
              style={{
                width: 'fit-content',
              }}
            />
          </div>
        </FormControl>
        {listCheckedPackage.some((item) =>
          aiDementiaPredictionId.includes(item),
        ) && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              marginLeft: 20,
            }}
          >
            {listPackageItem.some(
              (item) => item.packageItemId === EEG_DEMENTIA_PREDICTION_5_ID,
            ) && (
              <FormControlLabel
                control={
                  <Checkbox
                    name={EEG_DEMENTIA_PREDICTION_5_ID}
                    checked={listCheckedPackage.includes(
                      EEG_DEMENTIA_PREDICTION_5_ID,
                    )}
                    onChange={onCheckPackage}
                    color='primary'
                  />
                }
                label={
                  <div
                    style={{
                      fontSize: 16,
                    }}
                  >
                    Step 5{' '}
                    {calculatePackageAmount(EEG_DEMENTIA_PREDICTION_5_ID)}
                  </div>
                }
                style={{
                  width: '46%',
                }}
              />
            )}
            {listPackageItem.some(
              (item) => item.packageItemId === EEG_DEMENTIA_PREDICTION_8_ID,
            ) && (
              <FormControlLabel
                control={
                  <Checkbox
                    name={EEG_DEMENTIA_PREDICTION_8_ID}
                    checked={listCheckedPackage.includes(
                      EEG_DEMENTIA_PREDICTION_8_ID,
                    )}
                    onChange={onCheckPackage}
                    color='primary'
                  />
                }
                label={
                  <div
                    style={{
                      fontSize: 16,
                    }}
                  >
                    Step 8{' '}
                    {calculatePackageAmount(EEG_DEMENTIA_PREDICTION_8_ID)}
                  </div>
                }
                style={{
                  width: '46%',
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )

  const getListAssignedDeviceFromApi = async () => {
    const res = await getListAssignedDeviceApi()
    const listDuplicatedManu = res.list.map((item) => item.manufacturer)
    setListManufacturer(
      Array.from(
        new Set([`- ${t('IManufacturingCompany')} -`, ...listDuplicatedManu]),
      ),
    )
    const calibrationDeviceList = res.list.reduce<
      Record<
        string,
        {
          deviceId: number
          deviceName: string
        }[]
      >
    >((acc, cur) => {
      if (!acc[cur.manufacturer]) {
        acc[cur.manufacturer] = [
          {
            deviceId: -1,
            deviceName: `- ${t('IModelName')} -`,
          },
        ]
      }
      acc[cur.manufacturer].push({
        deviceId: cur.deviceId,
        deviceName: cur.deviceName,
      })
      return acc
    }, {})
    setListDevice({
      [`- ${t('IManufacturingCompany')} -`]: [
        {
          deviceId: -1,
          deviceName: `- ${t('IModelName')} -`,
        },
      ],
      ...calibrationDeviceList,
    })
  }

  const contentPackage: Record<string, React.ReactNode | null> = {
    [PACKAGE_TYPE.INDIVIDUAL]: renderIndividualSection(),
    [PACKAGE_TYPE.NORM]: renderNormSection(),
    [PACKAGE_TYPE.SUMMARY]: renderSummarySection(),
    [PACKAGE_TYPE.MCI5]: renderMciSection(),
  }

  const onSelectDevice = (value: string, type: 'manufacturer' | 'deviceId') => {
    setDeviceSelected((prev) => {
      const updated = {
        ...prev,
        [type]: type === 'deviceId' ? Number(value) : value,
      }
      if (type === 'manufacturer') {
        updated.deviceId = -1
      }
      return updated
    })
  }

  const validateFile = (fileList: File[]) => {
    if (fileList.length !== 1) {
      setHasFileError({
        isError: true,
        message: t('IFileUploadCountLimitErrorMessage', {count: 1}),
      })
      return false
    }

    const file = fileList[0]
    if (!file.name.toLowerCase().endsWith(FileExtension)) {
      setHasFileError({
        isError: true,
        message: t('IFileExtensionErrorMessage', {ext: FileExtension}),
      })
      return false
    }

    return true
  }

  const handleUploadingFile = async (fileList: File[]) => {
    if (!validateFile(fileList)) {
      return
    }

    setFile(fileList[0])
  }

  const onSubmitImport = async () => {
    let hasError = false
    if (!selectedPatient?.uuid) {
      setHasPatientError(true)
      hasError = true
      document.documentElement.scrollTo({top: 0, behavior: 'smooth'})
      document.body.scrollTo({top: 0, behavior: 'smooth'})
    }
    if (!file) {
      setHasFileError({
        isError: true,
        message: t('IEdfFileErrorMessage'),
      })
      hasError = true
    }
    if (!listCheckedPackage.length) {
      setHasAnalysisTypeError(true)
      hasError = true
    }
    if (
      deviceSelected.manufacturer !== `- ${t('IManufacturingCompany')} -` &&
      deviceSelected.deviceId === -1
    ) {
      setHasDeviceError(true)
      hasError = true
    }
    if (hasError) return
    setSuccessMessage({
      manufacturer:
        deviceSelected.manufacturer === `- ${t('IManufacturingCompany')} -`
          ? t('INoSelection')
          : deviceSelected.manufacturer,
      deviceName:
        deviceSelected.deviceId === -1
          ? t('INoSelection')
          : listDevice[deviceSelected.manufacturer]?.find(
              (device) => device.deviceId === deviceSelected.deviceId,
            )?.deviceName ?? '',
      fileName: file?.name ?? '',
    })
    setIsShowSuccessDialog(true)
  }

  const callUploadFileToS3 = async (file: File, types: string[]) => {
    const payload = {
      fileName: file.name,
      fileSize: file.size,
      types,
    }
    const res = await uploadMeasureFileApi(payload)
    if (!res.uploadingUrl) return {}

    await uploadInvoiceFileToS3({
      path: res.uploadingUrl,
      data: file,
    })

    return {
      fileId: res.id,
      fileName: file.name,
    }
  }

  const callRequestAnalysis = async (fileId: string) => {
    const payload = {
      calibrationDeviceId:
        deviceSelected.deviceId === -1 ? null : deviceSelected.deviceId,
      deviceId:
        deviceSelected.deviceId === -1 ? null : String(deviceSelected.deviceId),
      ecFileId: eyeStatus === 'EC' ? fileId : '',
      eoFileId: eyeStatus === 'EO' ? fileId : '',
      measureTime: measurementDate.getTime(),
      packageIds: [
        ...listCheckedPackage,
        ...(isEnableIndividual3d &&
        listPackageItem.some(
          (item) => item.packageItemId === EEG_INDICATOR_3D_ID,
        )
          ? [EEG_INDICATOR_3D_ID]
          : []),
        ...(isEnableNorm3d &&
        listPackageItem.some(
          (item) => item.packageItemId === NORM_PACKAGE_ID[1],
        ) &&
        listCheckedPackage.includes(NORM_PACKAGE_ID[0])
          ? [NORM_PACKAGE_ID[1]]
          : []),
      ],
      ppgFileId: '',
      icaYn: reAnalysis === 'manual' ? ('Y' as 'Y' | 'N') : ('N' as 'Y' | 'N'),
      sraYn: isSraYn ? ('Y' as 'Y' | 'N') : ('N' as 'Y' | 'N'),
      uuid: String(selectedPatient?.uuid),
      ...(listCheckedPackage.includes(NORM_PACKAGE_ID[0]) && {
        zScore: Number(zScore),
      }),
    }
    await edfImportAnalysisRequestApi(payload)
    navigate(RouteConstant.ANALYSIS_EEG_19_I.path)
  }

  const onConfirmImport = async () => {
    if (isImporting) return
    setIsImporting(true)
    try {
      if (!file) throw new Error(t('IEdfFileErrorMessage'))
      const {fileId} = await callUploadFileToS3(file, [eyeStatus])
      if (!fileId) throw new Error(t('IEdfFileErrorMessage'))
      await callRequestAnalysis(fileId)
    } catch (error) {
      // 성공 시에는 페이지 이동으로 언마운트되므로 실패 시에만 해제한다.
      setIsImporting(false)
      onFailureModalOpen(error.message)
    }
  }

  const onReset = () => {
    setSelectedPatient(null)
    setEyeStatus('EC')
    setMeasurementDate(new Date())
    setDeviceSelected({
      manufacturer: `- ${t('IManufacturingCompany')} -`,
      deviceId: -1,
    })
    setFile(null)
    setListCheckedPackage([])
  }

  useEffect(() => {
    if (eyeStatus === 'EO') {
      setListCheckedPackage([])
    }
  }, [eyeStatus])

  useEffect(() => {
    if (reAnalysis === 'manual') {
      setListCheckedPackage((prev) =>
        prev.filter(
          (item) =>
            ![
              ...NORM_PACKAGE_ID,
              ...reportGenerationId,
              ...aiDementiaPredictionId,
            ].includes(item),
        ),
      )
    }
  }, [reAnalysis])

  useEffect(() => {
    const IS_WHITE_LIST = TYPE_WHITE_LIST.every((item) =>
      JSON.parse(getFeatures() ?? '[]').includes(item),
    )
    setIsWhiteList(IS_WHITE_LIST)
    getListAssignedDeviceFromApi()
    getAvailableLicenses()
    getPackageList({
      target: TYPE_INDICATOR_PRODUCT.ALL,
    })
  }, [])

  useEffect(() => {
    getListAssignedDeviceFromApi()
    setDeviceSelected({
      manufacturer: `- ${t('IManufacturingCompany')} -`,
      deviceId: -1,
    })
  }, [t])

  useEffect(() => {
    setHasPatientError(false)
    setListCheckedPackage([])
  }, [selectedPatient])

  useEffect(() => {
    setHasFileError({
      isError: false,
      message: '',
    })
  }, [file])

  useEffect(() => {
    setHasAnalysisTypeError(false)
  }, [listCheckedPackage])

  useEffect(() => {
    setHasDeviceError(false)
  }, [deviceSelected])

  return (
    <CardWithTitle title={t('IEdfImport')}>
      <ul className={classes.listSquareContainer}>
        <li className={classes.listTitle}>
          <span className={classes.squareIcon}>■ </span>
          {t('ICustomerSelection')}
        </li>
        <PatientTable
          isWhiteList={isWhiteList}
          selectedPatient={selectedPatient}
          onSelectPatient={setSelectedPatient}
          hasPatientError={hasPatientError}
        />
        <li className={classes.listTitle}>
          {' '}
          <span className={classes.squareIcon}>■ </span>
          {t('IMeasurementInformation')}
        </li>
        <div className={classes.listMeasurement}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 30,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                flex: 1,
              }}
            >
              <div className={classes.listSubTitle}>* EC / EO</div>
              <RadioGroup
                defaultValue='EC'
                name='radio-buttons-group'
                row
                onChange={(e) => setEyeStatus(e.target.value)}
              >
                {listEyeStatus.map((item) => (
                  <FormControlLabel
                    key={item.value}
                    value={item.value}
                    control={<Radio color='primary' />}
                    label={item.label}
                  />
                ))}
              </RadioGroup>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                flex: 1,
              }}
            >
              <div className={classes.listSubTitle}>* {t('ICareDate')}</div>
              <div
                style={{
                  width: '46%',
                }}
              >
                <CalenderDatePicker
                  onChangeValue={(val) => setMeasurementDate(val ?? new Date())}
                  dateValue={measurementDate}
                  enablePassDate
                />
              </div>
            </div>
          </div>
          <div className={classes.listSubTitle}>
            * {t('IMeasuringEquipment')}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 40,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <Select
                value={deviceSelected.manufacturer}
                label={`- ${t('IManufacturingCompany')} -`}
                id='manufacturer-select'
                displayEmpty
                onChange={(e) => {
                  onSelectDevice(String(e.target.value), 'manufacturer')
                }}
                style={{
                  width: 300,
                  height: 40,
                }}
                input={
                  <OutlinedInput name='manufacturer' id='manufacturer-select' />
                }
              >
                {listManufacturer.map((item) => (
                  <MenuItem
                    value={item}
                    key={item}
                    style={{
                      height: 40,
                    }}
                  >
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <Select
                value={deviceSelected.deviceId}
                label={`- ${t('IModelName')} -`}
                displayEmpty
                onChange={(e) => {
                  onSelectDevice(String(e.target.value), 'deviceId')
                }}
                style={{
                  width: 300,
                  height: 40,
                }}
                input={<OutlinedInput name='model' id='model_name' />}
              >
                {(listDevice[deviceSelected.manufacturer] ?? []).map((item) => (
                  <MenuItem value={item.deviceId} key={item.deviceId}>
                    {item.deviceName}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
          {hasDeviceError && (
            <div className={classes.errorText}>
              * {t('IEdfDeviceErrorMessage')}
            </div>
          )}
          <div
            className={classes.listSubTitle}
            style={{
              marginTop: 30,
            }}
          >
            * {t('IFileUpload')}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
              width: '50%',
              marginBottom: 30,
            }}
          >
            <FileDropzone
              maxFileSizeMB={100}
              maxFileCount={1}
              acceptedFileExt={[FileExtension]}
              onChangeFiles={handleUploadingFile}
            />
            {hasFileError.isError && (
              <div className={classes.errorText}>* {hasFileError.message}</div>
            )}
            <div
              style={{
                fontSize: 14,
                color: 'gray',
                whiteSpace: 'pre-line',
              }}
            >
              Minimum data length: 30 sec{`\n`}
              Maximum file size: 100 MB{`\n`}
              File Type: .EDF with raw data{`\n`}
              Mandatory montage: Referencial montage{`\n`}
              Recommended reference: A linked-earlobe/Mastoid reference{`\n`}*
              Please check your EDF file satisfies the aforementioned conditions
            </div>
          </div>
          <li className={classes.listTitle}>
            <span className={classes.squareIcon}>■ </span>
            {t('IAnalysisInformation')}
          </li>
          {listPackageItem.some(
            (item) => item.packageItemId === EEG_INDICATOR_ID,
          ) && contentPackage[PACKAGE_TYPE.INDIVIDUAL]}
          {listPackageItem.some((item) =>
            NORM_PACKAGE_ID.includes(item.packageItemId),
          ) && contentPackage[PACKAGE_TYPE.NORM]}
          {listPackageItem.some((item) =>
            reportGenerationId.includes(item.packageItemId),
          ) && contentPackage[PACKAGE_TYPE.SUMMARY]}
          {listPackageItem.some((item) =>
            aiDementiaPredictionId.includes(item.packageItemId),
          ) && contentPackage[PACKAGE_TYPE.MCI5]}
          <div>※ {t('INumberParenthesesNote')}</div>
          <div>※ {t('IEyeOpenNote')}</div>
          <div>※ {t('IMciAdultNote')}</div>
          {hasAnalysisTypeError && (
            <div className={classes.errorText}>
              * {t('IEdfAnalysisTypeErrorMessage')}
            </div>
          )}
          <div className={classes.buttonWrapper}>
            <Button
              variant='contained'
              color='default'
              disableElevation
              onClick={onReset}
              style={{
                width: 200,
                height: 40,
              }}
            >
              {t('ICreateNew')}
            </Button>
            <Button
              variant='contained'
              color='primary'
              disableElevation
              onClick={onSubmitImport}
              style={{
                width: 200,
                height: 40,
              }}
            >
              {t('IAnalysis')}
            </Button>
          </div>
        </div>
      </ul>
      <ImportSuccessDialog
        open={isShowSuccessDialog}
        onClose={() => setIsShowSuccessDialog(false)}
        message={successMessage}
        onConfirm={onConfirmImport}
        loading={isImporting}
      />
    </CardWithTitle>
  )
}

export default EDFImport
